import { cn } from "@/lib/utils";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "outline" | "paper" | "ghost";

const base =
  "inline-flex items-center justify-center gap-2 font-sans text-sm font-medium " +
  "transition-[background-color,border-color,color,transform,opacity] duration-[160ms] " +
  "ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.985] " +
  "disabled:opacity-55 disabled:cursor-not-allowed " +
  "focus-visible:outline-2 focus-visible:outline-offset-2";

const variants: Record<Variant, string> = {
  primary:
    "bg-clay text-paper rounded-xs px-6 min-h-12 hover:bg-clay-dark focus-visible:outline-clay",
  outline:
    "rounded-xs border border-line-strong bg-transparent text-ink px-6 min-h-12 hover:border-ink focus-visible:outline-clay",
  paper:
    "bg-paper text-ink rounded-xs px-6 min-h-12 hover:bg-white focus-visible:outline-paper",
  ghost:
    "rounded-xs px-1 min-h-11 text-ink hover:text-clay focus-visible:outline-clay",
};

type BaseProps = {
  variant?: Variant;
  className?: string;
  children: ReactNode;
};

type LinkProps = BaseProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

type NativeButtonProps = BaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };

export function Button(props: LinkProps | NativeButtonProps) {
  const { variant = "primary", className, children, ...rest } = props;
  const classes = cn(base, variants[variant], className);

  if (props.href !== undefined) {
    return (
      <a className={classes} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {children}
      </a>
    );
  }

  return (
    <button className={classes} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>
      {children}
    </button>
  );
}
